import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Star, Clock, Users, Edit, Trash2, ChefHat, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecipeDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjustedServings, setAdjustedServings] = useState(null);
  const [adjustedIngredients, setAdjustedIngredients] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [userRating, setUserRating] = useState(0);

  // Calcular total de calorias baseado nas porções ajustadas
  const calculateTotalCalories = () => {
    if (!recipe?.nutrition?.per_serving?.calories) return null;
    const servings = adjustedServings || recipe.servings;
    return recipe.nutrition.per_serving.calories * servings;
  };

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipes/${id}`);
      setRecipe(response.data);
      setAdjustedServings(response.data.servings);
    } catch (error) {
      console.error('Error fetching recipe:', error);
      toast.error(t('recipe.errorLoadingRecipes'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustServings = async () => {
    if (adjustedServings === recipe.servings) {
      setAdjustedIngredients(null);
      return;
    }

    try {
      setAdjusting(true);
      const response = await axios.post(`${API}/recipes/${id}/adjust-servings`, {
        new_servings: adjustedServings
      });
      setAdjustedIngredients(response.data.ingredients);
      toast.success(`Porções ajustadas para ${adjustedServings}`);
    } catch (error) {
      console.error('Error adjusting servings:', error);
      toast.error(t('common.error'));
    } finally {
      setAdjusting(false);
    }
  };

  const handleFavorite = async () => {
    try {
      const response = await axios.post(`${API}/recipes/${id}/favorite`);
      setRecipe({ ...recipe, favorites_count: response.data.favorites_count });
      toast.success('Adicionado aos favoritos!');
    } catch (error) {
      console.error('Error favoriting recipe:', error);
      toast.error(t('common.error'));
    }
  };

  const handleRate = async (rating) => {
    try {
      const response = await axios.post(`${API}/recipes/${id}/rate`, { rating });
      setRecipe({ 
        ...recipe, 
        rating_average: response.data.rating_average,
        rating_count: response.data.rating_count
      });
      setUserRating(rating);
      toast.success(t('recipe.ratingSubmitted'));
    } catch (error) {
      console.error('Error rating recipe:', error);
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/recipes/${id}`);
      toast.success(t('recipe.recipeDeleted'));
      navigate('/');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Carregando receita...</p>
        </div>
      </div>
    );
  }

  if (!recipe) return null;

  const displayIngredients = adjustedIngredients || recipe.ingredients;
  const displayServings = adjustedServings || recipe.servings;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              data-testid="back-button"
              onClick={() => navigate('/')} 
              variant="ghost"
              className="hover:bg-orange-50"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Voltar
            </Button>
            
            <div className="flex items-center gap-2">
              <Button 
                data-testid="edit-recipe-button"
                onClick={() => navigate(`/edit/${id}`)} 
                variant="outline"
                className="border-orange-300 hover:bg-orange-50"
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    data-testid="delete-recipe-button"
                    variant="outline" 
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Deletar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Deletar Receita?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação não pode ser desfeita. A receita será permanentemente deletada.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Deletar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Image and Actions */}
          <div className="space-y-6">
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              {recipe.image_url ? (
                <img 
                  src={recipe.image_url} 
                  alt={recipe.title}
                  className="w-full aspect-square object-cover"
                />
              ) : (
                <div className="w-full aspect-square bg-gradient-to-br from-amber-200 to-orange-200 flex items-center justify-center">
                  <ChefHat className="w-32 h-32 text-white" />
                </div>
              )}
              <div className="absolute top-6 left-6 flex flex-col gap-3">
                <Badge className="bg-white/95 backdrop-blur-sm text-amber-900 px-4 py-2 text-base">
                  {recipe.category}
                </Badge>
                {recipe.user_name && (
                  <Badge className="bg-orange-600/95 backdrop-blur-sm text-white px-4 py-2 text-base flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {recipe.user_name}
                  </Badge>
                )}
              </div>
            </div>

            {/* Actions */}
            <Card className="p-6 bg-white/80 backdrop-blur-sm border-2 border-amber-100">
              <div className="flex items-center justify-between">
                <Button 
                  data-testid="favorite-button"
                  onClick={handleFavorite}
                  variant="outline"
                  className="flex-1 mr-2 border-2 border-red-300 hover:bg-red-50"
                >
                  <Heart className="w-5 h-5 mr-2 text-red-500" />
                  <span>{recipe.favorites_count}</span>
                </Button>
                
                <div className="flex-1 ml-2">
                  <div className="text-center mb-2 text-sm text-gray-600">Avaliar:</div>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        data-testid={`star-${star}`}
                        key={star}
                        onClick={() => handleRate(star)}
                        className="transition-transform hover:scale-125"
                      >
                        <Star 
                          className={`w-6 h-6 ${
                            star <= (userRating || recipe.rating_average)
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  {recipe.rating_count > 0 && (
                    <div className="text-center mt-1 text-xs text-gray-500">
                      {recipe.rating_average.toFixed(1)} ({recipe.rating_count} avaliações)
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Details */}
          <div className="space-y-8">
            {/* Title and Description */}
            <div>
              <h1 className="text-5xl font-bold text-amber-900 mb-4">{recipe.title}</h1>
              <p className="text-lg text-gray-700 leading-relaxed">{recipe.description}</p>
            </div>

            {/* Meta Info */}
            <div className="flex items-center gap-6 text-gray-600">
              {recipe.prep_time && (
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <span className="font-medium">
                    {recipe.prep_time + (recipe.cook_time || 0)} min
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                <span className="font-medium">{recipe.servings} porções</span>
              </div>
            </div>

            {/* Nutritional Info */}
            {recipe.nutrition?.per_serving && (
              <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
                <h3 className="text-xl font-semibold text-green-900 mb-4">Informações Nutricionais (por porção)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-700">{recipe.nutrition.per_serving.calories}</div>
                    <div className="text-sm text-gray-600 mt-1">Calorias</div>
                  </div>
                  {recipe.nutrition.per_serving.protein && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-700">{recipe.nutrition.per_serving.protein}</div>
                      <div className="text-sm text-gray-600 mt-1">Proteína</div>
                    </div>
                  )}
                  {recipe.nutrition.per_serving.carbs && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-700">{recipe.nutrition.per_serving.carbs}</div>
                      <div className="text-sm text-gray-600 mt-1">Carboidratos</div>
                    </div>
                  )}
                  {recipe.nutrition.per_serving.fat && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-700">{recipe.nutrition.per_serving.fat}</div>
                      <div className="text-sm text-gray-600 mt-1">Gordura</div>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-green-300 text-center">
                  <span className="text-gray-600">Total da receita ({adjustedServings || recipe.servings} porções): </span>
                  <span className="font-bold text-green-800">{calculateTotalCalories()} calorias</span>
                </div>
              </Card>
            )}

            {/* Servings Adjuster */}
            <Card className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200">
              <h3 className="text-xl font-semibold text-amber-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Ajustar Porções (IA)
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-bold text-orange-600 w-12">{adjustedServings}</span>
                  <Slider
                    data-testid="servings-slider"
                    value={[adjustedServings]}
                    onValueChange={(value) => setAdjustedServings(value[0])}
                    min={1}
                    max={20}
                    step={1}
                    className="flex-1"
                  />
                </div>
                <Button 
                  data-testid="adjust-servings-button"
                  onClick={handleAdjustServings}
                  disabled={adjusting || adjustedServings === recipe.servings}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {adjusting ? 'Ajustando...' : 'Ajustar com IA'}
                </Button>
              </div>
            </Card>

            {/* Ingredients */}
            <Card className="p-6 border-2 border-amber-100">
              <h3 className="text-2xl font-semibold text-amber-900 mb-4">
                Ingredientes ({displayServings} porções)
              </h3>
              <ul className="space-y-3">
                {displayIngredients.map((ing, index) => (
                  <li 
                    key={index} 
                    className="flex items-start gap-3 text-gray-700"
                  >
                    <span className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                    <span>
                      <span className="font-semibold">{ing.quantity} {ing.unit}</span> de {ing.name}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Instructions */}
            <Card className="p-6 border-2 border-amber-100">
              <h3 className="text-2xl font-semibold text-amber-900 mb-4">Modo de Preparo</h3>
              <ol className="space-y-4">
                {recipe.instructions.map((instruction, index) => (
                  <li key={index} className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </span>
                    <p className="text-gray-700 pt-1 leading-relaxed">{instruction}</p>
                  </li>
                ))}
              </ol>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailPage;